import { Component, computed, inject, OnInit, signal, WritableSignal } from '@angular/core';

import { getBucketsForAggregatedTerm, InventoryManagementState, UpdateInventoryManagementOfferListSelection } from 'remarketing-angular-library';
import { SearchFilterService } from '../../../services/search-filter/search-filter.service';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { Store } from '@ngxs/store';
import { TranslocoDirective, translateObjectSignal } from '@jsverse/transloco';
import { MatRadioModule } from '@angular/material/radio';
import { SlicePipe } from '@angular/common';
import { SearchFilterSectionComponent } from '../search-filter-section/search-filter-section.component';
import { OfferList } from '../../../interfaces/dealer-group.interface';

@Component({
  selector: 'app-shared-vehicles-filter',
  imports: [SlicePipe, TranslocoDirective, MatRadioModule, SearchFilterSectionComponent],
  templateUrl: './shared-vehicles-filter.component.html',
  styleUrl: './shared-vehicles-filter.component.scss'
})
export class SharedVehiclesFilterComponent extends SaComponent implements OnInit {
  private readonly searchFilterService = inject(SearchFilterService);
  private readonly store = inject(Store);

  /**
   * Selected value tracking:
   * - 'all' = All Recipients selected (no offer list filter)
   * - 'recipient:{dealerUuid}:{recipientUuid}' = All Lists for a specific recipient
   * - 'offerList:{offerListUuid}' = Specific offer list selected
   */
  readonly selectedValue$: WritableSignal<string> = signal('all');

  private readonly translations$ = translateObjectSignal(['sellModule.manageInventory.searchFilter']);

  // Computed Signals
  searchFilterTranslations = computed(() => {
    const translate = this.translations$();
    return {
      labelShowAllDealerCount: translate[0]?.labelShowAllDealerCount,
      labelDealers: translate[0]?.labelDealers,
      labelShowLess: translate[0]?.labelShowLess,
      labelAllLists: translate[0]?.labelAllList
    };
  });

  showAllOfferListsRecipientsText$ = computed(() => {
    return !this.showAllOfferListRecipients$()
      ? `${this.searchFilterTranslations().labelShowAllDealerCount} ${this.offerLists.length} ${
          this.searchFilterTranslations().labelDealers
        }`
      : this.searchFilterTranslations().labelShowLess;
  });

  // dealerUuids for any open offer list section
  protected openOfferListRecipientSections = new Set<string>();
  showAllOfferListRecipients$: WritableSignal<boolean> = signal(false);

  get offerLists() {
    return this.searchFilterService.getOfferLists();
  }

  ngOnInit(): void {
    this.setInitialSelectedValue();
  }

  /** Restore radio selection from state when navigating back */
  private setInitialSelectedValue(): void {
    const selectedOfferLists = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.offerLists;
    if (selectedOfferLists?.length > 0) {
      const radioButtonSelectedValue = this.store.selectSnapshot(InventoryManagementState.offerListSelection);
      if (radioButtonSelectedValue) {
        this.selectedValue$.set(radioButtonSelectedValue);
      }
    }
  }

  isOfferListDealershipDropdownOpen(dealerUuid: string) {
    return this.openOfferListRecipientSections.has(dealerUuid);
  }

  toggleOfferListDealershipDropdown(dealerUuid: string) {
    if (this.isOfferListDealershipDropdownOpen(dealerUuid)) {
      this.openOfferListRecipientSections.delete(dealerUuid);
    } else {
      this.openOfferListRecipientSections.add(dealerUuid);
    }
  }

  toggleShowAllOfferListRecipients() {
    this.showAllOfferListRecipients$.update(isShowAll => !isShowAll);
  }

    /** Select "All Recipients" (clear offer list filter) */
  selectAllRecipients(): void {
    this.selectedValue$.set('all');
    this.updateFilter();
  }

  /** Select "All Lists" for a specific recipient under a dealer */
  selectRecipientAllLists(dealerUuid: string, recipientUuid: string, offerLists: OfferList[]): void {
    this.selectedValue$.set(`recipient:${dealerUuid}:${recipientUuid}`);
    const offerListUuids = offerLists.map(offerList => offerList.value);
    this.updateFilter(offerListUuids);
  }

  /** Select a specific offer list */
  selectOfferList(offerListUuid: string): void {
    this.selectedValue$.set(`offerList:${offerListUuid}`);
    this.updateFilter([offerListUuid]);
  }

  /** Update the filter and persist the selection to state */
  private updateFilter(offerLists: string[] = []): void {
    this.store.dispatch(new UpdateInventoryManagementOfferListSelection(this.selectedValue$()));
    this.searchFilterService.setOfferLists(offerLists);
  }

  dealerById(index: number, dealer: any) {
    return dealer.value;
  }

  dealerListById(index: number, list: any) {
    return list.value;
  }

  offerListShowAllDataTrackId() {
    // string values do not need to be translated, as they are used for analytics only
    return this.showAllOfferListRecipients$() === true ? `Show All ${this.offerLists.length} Dealers` : 'Show Less';
  }
}
